import axios from 'axios';

// const API_BASE = 'https://basuat.ltra.gov.la/api';
const API_BASE = 'https://securityfee.info/api';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000, // Increased to 30 seconds for large dataset queries
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Log dashboard/service requests with month/year for verification (dev)
    const params = config.params || {};
    if (params.month != null || params.year != null) {
      const qs = Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';
      console.log('[Dashboard API]', config.method?.toUpperCase(), (config.baseURL || '') + (config.url || '') + qs);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);

    // Handle specific error cases
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login if needed
      localStorage.removeItem('authToken');
      // window.location.href = '/login';
    }

    return Promise.reject({
      message: error.response?.data?.error || error.message,
      status: error.response?.status,
      data: error.response?.data
    });
  }
);

// API endpoints
export const operatorAPI = {
  // Get all operators (optional params: month, year for subscriber/period filter)
  getAll: (params = {}) =>
    api.get('/operators', { params: Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')) }),

  // Get specific operator with details
  getById: (id) => api.get(`/operators/${id}`),

  // Get operator services
  getServices: (id) => api.get(`/operators/${id}/services`),
};

export const serviceAPI = {
  // Get all services
  getAll: () => api.get('/services'),

  // Get services by category (optional params: month, year)
  getByCategory: (category, params = {}) =>
    api.get(`/services/${category}`, { params: Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')) }),

  // Get operators for a service category
  getOperators: (category) => api.get(`/services/category/${category}/operators`),

  // Get service type details for MBB and FTB
  getServiceTypes: (params = {}) =>
    api.get('/services/service-types', { params: Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')) }),
};

export const dashboardAPI = {
  // Get dashboard summary
  getSummary: () => api.get('/dashboard/summary'),

  // Get operator dashboard data (optional params: month, year)
  getOperator: (id, params = {}) =>
    api.get(`/dashboard/operators/${id}`, { params: Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')) }),

  // Get chart data
  getCharts: () => api.get('/dashboard/charts'),
};

export const securityAPI = {

  // Get current security fee summary
  getCurrentSecurityFee: (params = {}) => api.get('/security/current-security-fees', { params: Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')) }),

  getSecurityFeeList: (params = {}) => api.get('/security/security-fees', { params: Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')) }),


};

export const feeCollectionAPI = {
  // Get fee collection overview data
  getOverview: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/fee-collection/overview${query ? `?${query}` : ''}`);
  },

  // Get fee collection by operator
  getByOperator: () => api.get('/fee-collection/by-operator'),

  // Get audit data (optional params: month, year - align with overview/by-operator)
  getAudit: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/fee-collection/audit${query ? `?${query}` : ''}`);
  },

  // Get charts data (optional params: month, year - e.g. ?month=12&year=2025)
  getCharts: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/fee-collection/charts${query ? `?${query}` : ''}`);
  },

  // Get subscriber eligibility for fee deduction
  getSubscriberEligibility: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/fee-collection/subscriber-eligibility${queryParams ? `?${queryParams}` : ''}`);
  },

  // Get security fee transactions for a subscriber by MSISDN
  getSubscriberTransactions: (msisdn, params = {}) => {
    const query = new URLSearchParams({ msisdn: String(msisdn), ...params }).toString();
    return api.get(`/fee-collection/subscriber-transactions?${query}`);
  },
};

export const monthlyAnalysisAPI = {
  // Get subscriber trends (optional params: month, year)
  getSubscriberTrends: (params = {}) =>
    api.get('/monthly-analysis/subscriber-trends', { params: Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')) }),

  getRevenueTrends: (params = {}) =>
    api.get('/monthly-analysis/revenue-trends', { params: Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')) }),

  getCurrentSummary: (params = {}) =>
    api.get('/monthly-analysis/current-summary', { params: Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')) }),

  getYtdPerformance: (params = {}) =>
    api.get('/monthly-analysis/ytd-performance', { params: Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')) }),
};

export const securitfeeAPI = {
  // Get security fee collection overview (accepts optional query params: month, year, period, etc.)
  getOverview: (params = {}) => {
    // API supports only month and year as query params for overview; ignore other keys.
    const allowed = {};
    if (params.month !== undefined) allowed.month = params.month;
    if (params.year !== undefined) allowed.year = params.year;
    const query = new URLSearchParams(allowed).toString();
    return api.get(`/fee-collection/overview${query ? `?${query}` : ''}`);
  },

  // Get security fee collection by operator
  getByOperator: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/fee-collection/by-operator${query ? `?${query}` : ''}`);
  },

  // Get security fee audit data (optional params: month, year)
  getAudit: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/fee-collection/audit${query ? `?${query}` : ''}`);
  },

  // Get security fee charts data (optional params: month, year)
  getCharts: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/fee-collection/charts${query ? `?${query}` : ''}`);
  },

  // Get security fees data (from security endpoint)
  getFees: () => api.get('/security/fees'),

  // Get security fee transactions for a subscriber by MSISDN
  getSecurityFeeTransactions: (msisdn, params = {}) => {
    const query = new URLSearchParams({ msisdn: String(msisdn), ...params }).toString();
    return api.get(`/security/fees/${msisdn}?${query}`);
  },

  // Get low balance risk data
  getLowBalance: () => api.get('/security/low-balance'),
};

export const subscriberAPI = {
  // Get all subscribers with pagination and filters
  getTotalSubscribers: () => {
    return api.get(`/subscribers/current-subscribers`);
  },

  getAll: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/subscribers${queryParams ? `?${queryParams}` : ''}`);
  },

  // Get specific subscriber details
  getById: (id) => api.get(`/subscribers/${id}`),

  // Create new subscriber
  create: (data) => api.post('/subscribers', data),

  // Update subscriber
  update: (id, data) => api.put(`/subscribers/${id}`, data),

  // Delete subscriber (soft delete)
  delete: (id) => api.delete(`/subscribers/${id}`),

  report: (params = {}) => api.get('/subscribers/report', { params: Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')) }),
};

export const securityFeeConfigAPI = {
  // Get all security fee configurations
  getAll: () => api.get('/security-fee-config'),

  // Get operators for dropdown
  getOperators: () => api.get('/security-fee-config/operators'),

  // Get summary statistics
  getSummary: () => api.get('/security-fee-config/summary'),

  // Create new configuration
  create: (data) => api.post('/security-fee-config', data),
};

// Utility: format amount as 300K, 1M, 1B (handles negative values)
export const formatCurrency = (amount) => {
  if (amount == null || amount === '') return '0 ₭';
  const num = parseFloat(amount);
  if (Number.isNaN(num)) return '0 ₭';
  const isNegative = num < 0;
  const absNum = Math.abs(num);
  if (absNum === 0) return '0 ₭';
  let suffix;
  let value;
  if (absNum >= 1000000000000) {
    value = absNum / 1000000000000;
    suffix = 'T';
  } else if (absNum >= 1000000000) {
    value = absNum / 1000000000;
    suffix = 'B';
  } else if (absNum >= 1000000) {
    value = absNum / 1000000;
    suffix = 'M';
  } else if (absNum >= 1000) {
    value = absNum / 1000;
    suffix = 'K';
  } else {
    const formatted = new Intl.NumberFormat('en-US').format(Math.round(absNum));
    return (isNegative ? '-' : '') + formatted + ' ₭';
  }
  const str = value % 1 === 0 ? String(Math.round(value)) : value.toFixed(1).replace(/\.0$/, '');
  return (isNegative ? '-' : '') + str + suffix + ' ₭';
};

export const formatNumber = (num) => {
  if (!num && num !== 0) return '0';
  return new Intl.NumberFormat('en-US').format(parseInt(num));
};

export const getComplianceStatus = (rate) => {
  if (rate >= 98) return {
    status: 'excellent',
    color: 'green',
    text: 'Excellent',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800'
  };
  if (rate >= 95) return {
    status: 'good',
    color: 'blue',
    text: 'Good',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800'
  };
  if (rate >= 90) return {
    status: 'warning',
    color: 'yellow',
    text: 'Needs Review',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-800'
  };
  return {
    status: 'critical',
    color: 'red',
    text: 'Critical',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800'
  };
};

// Health check (same host as api; /health at origin - adjust if backend uses /api/health)
export const healthCheck = () => {
  const base = API_BASE.replace(/\/api\/?$/, '');
  return axios.get(`${base}/health`);
};

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  me: () => api.get('/auth/me'),
};

// Users API (admin)
export const usersAPI = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/users${query ? `?${query}` : ''}`);
  },
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  remove: (id) => api.delete(`/users/${id}`),
};

// Roles API (admin)
export const rolesAPI = {
  list: () => api.get('/roles'),
  getPermissions: () => api.get('/roles/permissions'),
  getRolePermissions: (roleId) => api.get(`/roles/${roleId}/permissions`),
  updateRolePermissions: (roleId, permissions) => api.put(`/roles/${roleId}/permissions`, { permissions }),
  create: (data) => api.post('/roles', data),
  remove: (id) => api.delete(`/roles/${id}`),
};

// Audit API
export const auditAPI = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/audit${query ? `?${query}` : ''}`);
  },
  getStats: () => api.get('/audit/stats'),
  create: (data) => api.post('/audit', data),
  export: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/audit/export${query ? `?${query}` : ''}`);
  },
};

// File Processing API
export const fileProcessingAPI = {
  // Get all file processing records with pagination and filters
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/file-processing${query ? `?${query}` : ''}`);
  },

  // Get specific file processing details
  getById: (id) => api.get(`/file-processing/${id}`),

  // Get file processing logs
  getLogs: (id) => api.get(`/file-processing/${id}/logs`),

  // Retry failed file processing
  retry: (id) => api.post(`/file-processing/${id}/retry`),

  // Get statistics
  getStats: () => api.get('/file-processing/stats'),

  // Get files by status
  getByStatus: (status) => api.get(`/file-processing/status/${status}`),

  // Get files by source operator
  getBySource: (source) => api.get(`/file-processing/source/${source}`),
};

export default api;
